import { targets, useReferentialContext } from "../../Context";
import React, { useEffect, useState } from "react";
import {
    CompetitiveGames,
    CompetitiveRoundSummaries,
    CompetitiveRoundSummariesDeathEventAgnosticInner,
    CompetitiveRoundSummariesDeathEventInner,
    Maps,
    ScrimGames,
    ScrimRoundSummaries,
    Team
} from "../../reckon-api";
import WindowContainer from "../../components/layout/WindowContainer";
import { MultipleGameSelectorDialog } from "../../components/selectors/Competitive/SpecificGameSelectorDialog";
import { capitalize, Chip, Grid, IconButton, Stack, Typography, useTheme } from "@mui/material";
import { NeonToggleButton } from "../../NeonComponents/NeonToggle";
import { NeonCard, NeonCardContent, NeonCardHeader } from "../../NeonComponents/NeonCards";
import { scrollable, getNeonColor, attacker_color, defender_color } from "../../theme";
import { getPlayerPointColor, HeatmapKillsRender, KillEventWithLocation, KillMapRenderMode, KillPositionSource, SelectableKillEvent } from "../../components/Maps/HeatmapKillsRender";
import Box from "@mui/material/Box";
import { NeonTab, NeonTabs } from "../../NeonComponents/NeonTabs";
import { ExpandLess, ExpandMore, GpsFixed, Visibility, VisibilityOff } from "@mui/icons-material";
import AgentIcon from "../../components/Icons/AgentIcon";
import TeamIcon from "../../components/Icons/TeamIcon";
import WeaponIcon from "../../components/Icons/WeaponIcon";
import { formatDuration } from "../../utils";
import { getMapAsset } from "../../valorant-api/mapAssets";

type HeatmapGame = CompetitiveGames | ScrimGames;
type HeatmapRoundSummary = CompetitiveRoundSummaries | ScrimRoundSummaries;

type KillDisplayMode = 'kills' | 'deaths' | 'both';
type SideFilter = 'all' | 'Attacker' | 'Defender';
type PlantFilter = 'all' | 'pre' | 'post';

type TeamMapPlayer = {
    id: string;
    kills: number;
    deaths: number;
    agents: string[];
};

type ListedKillEvent = KillEventWithLocation & {
    eventType: 'kill' | 'death';
    roundEcoType: string;
    roundIndex: number;
    player: string;
    ownTeam: string;
    opponent: string;
    plantPhase: 'pre' | 'post';
};

function displayPlayerName(name?: string | null) {
    const normalized = (name || '').trim();
    if (!normalized || /^\d+$/.test(normalized)) return '';
    return normalized;
}

type AgnosticKillEventRuntime = CompetitiveRoundSummariesDeathEventAgnosticInner & {
    killer_agent?: string | null;
    is_headshot?: boolean;
};

/** Scrim agnostic events use a flatter location shape — normalize to the competitive kill event model. */
function normalizeAgnosticKillEvent(
    event: CompetitiveRoundSummariesDeathEventAgnosticInner,
    fallbackKillerAgent?: string | null,
): KillEventWithLocation {
    const runtimeEvent = event as AgnosticKillEventRuntime;
    const killerPos = event.killer_location?.position;
    return {
        // Schema omits killer_agent on agnostic events; runtime may include it.
        // For kills rows, summary.agent is the killer agent.
        killer_agent: runtimeEvent.killer_agent || fallbackKillerAgent || null,
        killer_location: {
            position: {
                x: killerPos?.x ?? null,
                y: killerPos?.y ?? null,
                z: null,
            },
            view_vector: { x: null, y: null, z: null },
            velocity: { x: null, y: null, z: null },
            timestamp: null,
            time_in_round: null,
        },
        killer_weapon: event.killer_weapon,
        killer_ability: event.killer_ability,
        is_headshot: !!runtimeEvent.is_headshot,
        was_traded: event.was_traded,
        is_first_blood: event.is_first_blood,
        is_first_true_blood: event.is_first_true_blood,
        victim_agent: event.victim_agent,
        victim_weapon: event.victim_weapon,
        victim_location: {
            position: {
                x: event.victim_location?.x ?? null,
                y: event.victim_location?.y ?? null,
                z: null,
            },
            view_vector: { x: null, y: null, z: null },
            velocity: { x: null, y: null, z: null },
            timestamp: null,
            time_in_round: null,
        },
        victim_player: '',
        assistants: [],
        time_in_round: event.time_in_round,
        situation_before_kill: event.situation_before_kill,
        situation_after_kill: event.situation_after_kill,
    };
}

const ECO_TYPE_ORDER = [
    'pistol',
    'eco',
    'save',
    'force',
    'force buy',
    'forcebuy',
    'bonus',
    'semi',
    'semi buy',
    'semibuy',
    'full',
    'full buy',
    'fullbuy',
];

function getEventWeapon(weapon: string | null | undefined) {
    const normalized = (weapon || '').trim();
    return normalized || 'Unknown';
}

/** Valorant shop prices in credits; unknown/ability weapons sort last. */
const WEAPON_PRICE: Record<string, number> = {
    classic: 0,
    melee: 0,
    knife: 0,
    'tactical knife': 0,
    shorty: 300,
    frenzy: 450,
    ghost: 500,
    bandit: 600,
    sheriff: 800,
    bucky: 850,
    marshal: 950,
    stinger: 1100,
    spectre: 1600,
    ares: 1600,
    judge: 1850,
    bulldog: 2050,
    guardian: 2250,
    outlaw: 2400,
    phantom: 2900,
    vandal: 2900,
    odin: 3200,
    operator: 4700,
};

function getWeaponPrice(weapon: string) {
    return WEAPON_PRICE[weapon.toLowerCase()] ?? Number.POSITIVE_INFINITY;
}

function compareWeaponsByPrice(a: string, b: string) {
    const priceDiff = getWeaponPrice(a) - getWeaponPrice(b);
    if (priceDiff !== 0) return priceDiff;
    return a.localeCompare(b);
}

function getUniqueWeapons(weapons: Array<string | null | undefined>) {
    return Array.from(new Set(weapons.map(getEventWeapon))).sort(compareWeaponsByPrice);
}

function pruneSelectedWeapons(selected: Set<string>, available: Set<string>) {
    if (selected.size === 0) return selected;
    const next = new Set([...selected].filter((weapon) => available.has(weapon)));
    return next.size === selected.size ? selected : next;
}

function toggleSetValue(prev: Set<string>, value: string) {
    const next = new Set(prev);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    return next;
}

function getEcoTypeSortRank(ecoType: string | undefined) {
    const normalized = (ecoType || '').toLowerCase().trim();
    if (!normalized) return 999;
    if (normalized.includes('pistol')) return 0;
    const exact = ECO_TYPE_ORDER.findIndex((type) => type === normalized);
    if (exact !== -1) return exact;
    const partial = ECO_TYPE_ORDER.findIndex((type) => normalized.includes(type));
    return partial === -1 ? 500 : partial;
}

export default function KillsHeatmap(props: { target: targets }) {
    const context = useReferentialContext();
    const theme = useTheme();
    const neonColor = getNeonColor(theme);
    const roundSummariesApi = props.target === targets.scrims
        ? context.reckonApi.scrimRoundSummaries
        : context.reckonApi.competitiveRoundSummaries;

    // Selection state
    const [team, setTeam] = useState(context.selectedTeam as Team | null);
    const [games, setGames] = useState([] as HeatmapGame[]);
    const [players, setPlayers] = useState([] as string[]);
    const [teamPlayers, setTeamPlayers] = useState([] as TeamMapPlayer[]);
    const [sideFilter, setSideFilter] = useState<SideFilter>('all');
    const [displayMode, setDisplayMode] = useState<KillDisplayMode>('kills');
    const [renderMode, setRenderMode] = useState<KillMapRenderMode>('heatmap');
    const [positionSource, setPositionSource] = useState<KillPositionSource>('victim');
    const [plantFilter, setPlantFilter] = useState<PlantFilter>('all');
    const [selectedKillerWeapons, setSelectedKillerWeapons] = useState(() => new Set<string>());
    const [selectedVictimWeapons, setSelectedVictimWeapons] = useState(() => new Set<string>());
    const [selectedMap, setSelectedMap] = useState<Maps | undefined>(undefined);
    
    // Data state
    const [roundSummaries, setRoundSummaries] = useState([] as HeatmapRoundSummary[]);
    const [killEvents, setKillEvents] = useState([] as ListedKillEvent[]);
    const [disabledKillIndexes, setDisabledKillIndexes] = useState(() => new Set<number>());
    const [collapsedEcoTypes, setCollapsedEcoTypes] = useState(() => new Set<string>());
    const [hoveredEventIndex, setHoveredEventIndex] = useState<number | null>(null);
    const [loading, setLoading] = useState(false);

    // Get unique maps from selected games
    const uniqueMaps = [...new Set(games.map(g => g.map))];
    
    // When games change, auto-select map if only one
    useEffect(() => {
        if (uniqueMaps.length === 1) {
            const map = context.mapsReferential.find(m => m.id === uniqueMaps[0]);
            setSelectedMap(map);
        } else if (uniqueMaps.length > 1 && !selectedMap) {
            const map = context.mapsReferential.find(m => m.id === uniqueMaps[0]);
            setSelectedMap(map);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [games, context.mapsReferential]);

    // Fetch round summaries when games/players/side change
    useEffect(() => {
        if (games.length === 0 || !team) {
            setRoundSummaries([]);
            setKillEvents([]);
            setDisabledKillIndexes(new Set());
            setCollapsedEcoTypes(new Set());
            return;
        }

        setLoading(true);

        // Filter games by selected map
        const filteredGames = selectedMap 
            ? games.filter(g => g.map === selectedMap.id)
            : games;

        if (filteredGames.length === 0) {
            setRoundSummaries([]);
            setKillEvents([]);
            setDisabledKillIndexes(new Set());
            setCollapsedEcoTypes(new Set());
            setLoading(false);
            return;
        }

        const gameIds = filteredGames.map(g => g.id).join(',');
        const useAgnosticEvents = props.target === targets.scrims;
        const killFields = useAgnosticEvents
            ? 'kills_agnostic,death_event_agnostic'
            : 'kill_events,death_event';
        const requestParams: any = {
            additional_filters: {
                game__in: gameIds,
                team1: team.id,
            },
            // Only fetch the fields we need for the heatmap
            only_values: [`${killFields},kills,agent,round_eco_type_no_bonus,round_index,player,team1,team2,plant_time,game,round_id,freeze_time_end_timestamp`],
        };

        // Add player filter if players selected
        if (players.length > 0) {
            requestParams.additional_filters.player__in = players.join(',');
        }

        // Add side filter
        if (sideFilter !== 'all') {
            requestParams.additional_filters.side = sideFilter;
        }

        // Plant time is usually only on the planter's row (any team). Fetch it separately
        // without team1/player filters so opponent plants are still available.
        const plantRequestParams: any = {
            additional_filters: {
                game__in: gameIds,
                planted_spike: true,
            },
            only_values: ['plant_time,freeze_time_end_timestamp,game,round_index,round_id'],
        };

        const roundKeysFor = (summary: { round_id?: string; game?: string; round_index?: number }) => {
            const keys: string[] = [];
            if (summary.round_id) keys.push(`id:${summary.round_id}`);
            if (summary.game != null && summary.round_index != null) {
                keys.push(`gr:${summary.game}-${summary.round_index}`);
            }
            return keys;
        };

        Promise.all([
            roundSummariesApi.list(requestParams),
            roundSummariesApi.list(plantRequestParams),
        ])
            .then(([summaries, plantSummaries]) => {
                console.log('Received summaries:', summaries.length);
                if (summaries.length > 0) {
                    console.log('First summary keys:', Object.keys(summaries[0]));
                    console.log(
                        useAgnosticEvents ? 'First summary kills_agnostic:' : 'First summary kill_events:',
                        useAgnosticEvents ? summaries[0].kills_agnostic : summaries[0].kill_events,
                    );
                    console.log(
                        useAgnosticEvents ? 'First summary death_event_agnostic:' : 'First summary death_event:',
                        useAgnosticEvents ? summaries[0].death_event_agnostic : summaries[0].death_event,
                    );
                    console.log('Sample plant_time/freeze:', summaries[0].plant_time, summaries[0].freeze_time_end_timestamp);
                }
                console.log('Plant summaries received:', plantSummaries.length);
                setRoundSummaries(summaries);

                // Absolute plant timestamp in the same domain as kill time_in_round
                // (plant_time is relative to freeze end — same convention as CheckGames).
                const absolutePlantByRound = new Map<string, number>();
                const registerPlant = (summary: HeatmapRoundSummary) => {
                    if (summary.plant_time == null) return;
                    const freezeEnd = summary.freeze_time_end_timestamp || 0;
                    // plant_time is offset from freeze end; time_in_round includes freeze.
                    const absolutePlant = summary.plant_time + freezeEnd;
                    roundKeysFor(summary).forEach((key) => {
                        absolutePlantByRound.set(key, absolutePlant);
                    });
                };
                plantSummaries.forEach(registerPlant);
                summaries.forEach(registerPlant);

                const lookupPlantTime = (summary: HeatmapRoundSummary) => {
                    for (const key of roundKeysFor(summary)) {
                        const value = absolutePlantByRound.get(key);
                        if (value != null) return value;
                    }
                    return undefined;
                };

                const toKillEvent = (
                    event: CompetitiveRoundSummariesDeathEventInner | CompetitiveRoundSummariesDeathEventAgnosticInner,
                    fallbackKillerAgent?: string | null,
                ): KillEventWithLocation => {
                    if (useAgnosticEvents) {
                        return normalizeAgnosticKillEvent(
                            event as CompetitiveRoundSummariesDeathEventAgnosticInner,
                            fallbackKillerAgent,
                        );
                    }
                    return event as CompetitiveRoundSummariesDeathEventInner;
                };

                // Extract kill events based on display mode
                const events: ListedKillEvent[] = [];
                
                summaries.forEach(summary => {
                    const roundEcoType = (summary.round_eco_type_no_bonus || summary.round_eco_type || '').trim();
                    const roundIndex = summary.round_index || 0;
                    const player = summary.player || '';
                    const summaryAgent = summary.agent || '';
                    const ownTeam = summary.team1 || team.id;
                    const opponent = summary.team2 || '';
                    const absolutePlantTime = lookupPlantTime(summary);
                    const getPlantPhase = (timeInRound: number): 'pre' | 'post' =>
                        absolutePlantTime != null && timeInRound >= absolutePlantTime ? 'post' : 'pre';

                    if (displayMode === 'deaths' || displayMode === 'both') {
                        const deathEvents = useAgnosticEvents
                            ? summary.death_event_agnostic
                            : summary.death_event;
                        if (deathEvents && Array.isArray(deathEvents)) {
                            deathEvents.forEach((deathEvent) => {
                                // On death rows, summary.agent is the victim — only use event.killer_agent.
                                const normalized = toKillEvent(deathEvent);
                                events.push({
                                    ...normalized,
                                    eventType: 'death',
                                    roundEcoType,
                                    roundIndex,
                                    player,
                                    ownTeam,
                                    opponent,
                                    plantPhase: getPlantPhase(normalized.time_in_round || 0),
                                });
                            });
                        }
                    }
                    
                    if (displayMode === 'kills' || displayMode === 'both') {
                        const killEventsForSummary = useAgnosticEvents
                            ? summary.kills_agnostic
                            : summary.kill_events;
                        if (killEventsForSummary && Array.isArray(killEventsForSummary)) {
                            killEventsForSummary.forEach((killEvent) => {
                                // On kill rows, summary.agent is the killer.
                                const normalized = toKillEvent(killEvent, summaryAgent);
                                events.push({
                                    ...normalized,
                                    eventType: 'kill',
                                    roundEcoType,
                                    roundIndex,
                                    player,
                                    ownTeam,
                                    opponent,
                                    plantPhase: getPlantPhase(normalized.time_in_round || 0),
                                });
                            });
                        }
                    }
                });

                events.sort((a, b) => {
                    const ecoDiff = getEcoTypeSortRank(a.roundEcoType) - getEcoTypeSortRank(b.roundEcoType);
                    if (ecoDiff !== 0) return ecoDiff;
                    const typeNameDiff = (a.roundEcoType || '').localeCompare(b.roundEcoType || '');
                    if (typeNameDiff !== 0) return typeNameDiff;
                    const roundDiff = (a.roundIndex || 0) - (b.roundIndex || 0);
                    if (roundDiff !== 0) return roundDiff;
                    return (a.time_in_round || 0) - (b.time_in_round || 0);
                });

                console.log('Total events collected:', events.length);
                console.log('Rounds with plant time:', absolutePlantByRound.size);
                if (events.length > 0) {
                    console.log('First event structure:', JSON.stringify(events[0], null, 2));
                }
                setKillEvents(events);
                setDisabledKillIndexes(new Set());
                setCollapsedEcoTypes(new Set());
                setLoading(false);
            })
            .catch(err => {
                console.error('Error fetching round summaries:', err);
                setLoading(false);
            });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [games, team, players, sideFilter, displayMode, selectedMap, props.target]);

    // Load players + side-aware K/D for the selected map/games
    useEffect(() => {
        if (!team || games.length === 0) {
            setTeamPlayers([]);
            setPlayers([]);
            return;
        }

        const filteredGames = selectedMap
            ? games.filter(g => g.map === selectedMap.id)
            : games;

        if (filteredGames.length === 0) {
            setTeamPlayers([]);
            setPlayers([]);
            return;
        }

        const playerStatsParams: any = {
            additional_filters: {
                game__in: filteredGames.map(g => g.id).join(','),
                team1: team.id,
            },
            // Round summaries respect side; game summaries do not.
            only_values: ['player,agent,kills,deaths'],
        };
        if (sideFilter !== 'all') {
            playerStatsParams.additional_filters.side = sideFilter;
        }

        roundSummariesApi.list(playerStatsParams).then(summaries => {
            const byPlayer = new Map<string, TeamMapPlayer>();

            summaries.forEach(summary => {
                const existing = byPlayer.get(summary.player);
                if (existing) {
                    existing.kills += summary.kills || 0;
                    existing.deaths += summary.deaths || 0;
                    if (summary.agent && !existing.agents.includes(summary.agent)) {
                        existing.agents.push(summary.agent);
                    }
                } else {
                    byPlayer.set(summary.player, {
                        id: summary.player,
                        kills: summary.kills || 0,
                        deaths: summary.deaths || 0,
                        agents: summary.agent ? [summary.agent] : [],
                    });
                }
            });

            setTeamPlayers(Array.from(byPlayer.values()));
            setPlayers(prev => prev.filter(id => byPlayer.has(id)));
        }).catch(err => {
            console.error('Error fetching team map players:', err);
            setTeamPlayers([]);
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [team, games, selectedMap, sideFilter, props.target]);

    // Toggle player selection
    const togglePlayer = (playerId: string) => {
        if (players.includes(playerId)) {
            setPlayers(players.filter(id => id !== playerId));
        } else {
            setPlayers([...players, playerId]);
        }
    };

    const sortedTeamPlayers = [...teamPlayers].sort((a, b) => {
        if (displayMode === 'deaths') return b.deaths - a.deaths;
        return b.kills - a.kills;
    });

    const availableKillerWeapons = getUniqueWeapons(killEvents.map((event) => event.killer_weapon));
    const availableVictimWeapons = getUniqueWeapons(killEvents.map((event) => event.victim_weapon));

    useEffect(() => {
        const killerWeapons = new Set(killEvents.map((event) => getEventWeapon(event.killer_weapon)));
        const victimWeapons = new Set(killEvents.map((event) => getEventWeapon(event.victim_weapon)));
        setSelectedKillerWeapons((prev) => pruneSelectedWeapons(prev, killerWeapons));
        setSelectedVictimWeapons((prev) => pruneSelectedWeapons(prev, victimWeapons));
    }, [killEvents]);

    const filteredKillIndexes = killEvents
        .map((event, index) => ({ event, index }))
        .filter(({ event }) => {
            if (plantFilter !== 'all' && event.plantPhase !== plantFilter) return false;
            if (selectedKillerWeapons.size > 0 && !selectedKillerWeapons.has(getEventWeapon(event.killer_weapon))) return false;
            if (selectedVictimWeapons.size > 0 && !selectedVictimWeapons.has(getEventWeapon(event.victim_weapon))) return false;
            return true;
        })
        .map(({ index }) => index);

    const renderWeaponFilter = (
        label: string,
        availableWeapons: string[],
        selectedWeapons: Set<string>,
        onToggle: (weapon: string) => void,
        onClear: () => void,
    ) => {
        if (availableWeapons.length === 0) return null;
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.35, flexShrink: 0 }}>
                <Typography sx={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.45)', px: 0.25 }}>
                    {label}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    <NeonToggleButton
                        value="all"
                        selected={selectedWeapons.size === 0}
                        onClick={onClear}
                        sx={{ fontSize: '0.65rem', py: 0.35, px: 0.75, minWidth: 0 }}
                    >
                        All
                    </NeonToggleButton>
                    {availableWeapons.map((weapon) => (
                        <NeonToggleButton
                            key={`${label}-${weapon}`}
                            value={weapon}
                            selected={selectedWeapons.has(weapon)}
                            onClick={() => onToggle(weapon)}
                            sx={{ fontSize: '0.65rem', py: 0.35, px: 0.75, minWidth: 0 }}
                            title={weapon}
                        >
                            {weapon === 'Unknown' ? (
                                'Unknown'
                            ) : (
                                <WeaponIcon weapon={weapon} width={28} height={14} />
                            )}
                        </NeonToggleButton>
                    ))}
                </Box>
            </Box>
        );
    };

    const visibleKillEvents = filteredKillIndexes
        .filter((index) => !disabledKillIndexes.has(index))
        .map((index) => killEvents[index]);
    const mapSelectableKills: SelectableKillEvent[] = filteredKillIndexes.map((index) => {
        const event = killEvents[index];
        return {
            ...event,
            selectionIndex: index,
            enabled: !disabledKillIndexes.has(index),
            player: event.player,
        };
    });
    const allKillsEnabled = filteredKillIndexes.length > 0 && filteredKillIndexes.every((index) => !disabledKillIndexes.has(index));
    const noKillsEnabled = filteredKillIndexes.length > 0 && filteredKillIndexes.every((index) => disabledKillIndexes.has(index));

    const killEventGroups = (() => {
        const byType = new Map<string, number[]>();
        filteredKillIndexes.forEach((index) => {
            const ecoType = (killEvents[index].roundEcoType || '').trim() || 'Other';
            const existing = byType.get(ecoType);
            if (existing) existing.push(index);
            else byType.set(ecoType, [index]);
        });

        return Array.from(byType.entries())
            .map(([ecoType, indexes]) => ({ ecoType, indexes }))
            .sort((a, b) => {
                const rankDiff = getEcoTypeSortRank(a.ecoType) - getEcoTypeSortRank(b.ecoType);
                if (rankDiff !== 0) return rankDiff;
                return a.ecoType.localeCompare(b.ecoType);
            });
    })();

    const toggleKillEvent = (index: number) => {
        setDisabledKillIndexes(prev => {
            const next = new Set(prev);
            if (next.has(index)) next.delete(index);
            else next.add(index);
            return next;
        });
    };

    const setAllKillEventsEnabled = (enabled: boolean) => {
        setDisabledKillIndexes(prev => {
            const next = new Set(prev);
            filteredKillIndexes.forEach((index) => {
                if (enabled) next.delete(index);
                else next.add(index);
            });
            return next;
        });
    };

    const toggleEcoTypeCollapsed = (ecoType: string) => {
        setCollapsedEcoTypes(prev => {
            const next = new Set(prev);
            if (next.has(ecoType)) next.delete(ecoType);
            else next.add(ecoType);
            return next;
        });
    };

    const setEcoTypeEnabled = (indexes: number[], enabled: boolean) => {
        setDisabledKillIndexes(prev => {
            const next = new Set(prev);
            indexes.forEach((index) => {
                if (enabled) next.delete(index);
                else next.add(index);
            });
            return next;
        });
    };

    const handleMarqueeSelect = (selectedIndexes: number[]) => {
        const selected = new Set(selectedIndexes);
        setDisabledKillIndexes(prev => {
            const next = new Set(prev);
            filteredKillIndexes.forEach((index) => {
                if (selected.has(index)) next.delete(index);
                else next.add(index);
            });
            return next;
        });
    };

    // Stats summary
    // kills is now a number, not an array
    const totalKills = roundSummaries.reduce((acc, s) => acc + (s.kills || 0), 0);
    const totalDeaths = roundSummaries.reduce((acc, s) => {
        const deaths = props.target === targets.scrims
            ? s.death_event_agnostic
            : s.death_event;
        return acc + (deaths?.length || 0);
    }, 0);
    const headshotKills = killEvents.filter(k => k.is_headshot).length;
    const firstBloods = killEvents.filter(k => k.is_first_blood).length;

    return (
        <WindowContainer
            direction="column"
            spacing={1}
            sx={{ height: '100%', overflow: 'hidden', boxSizing: 'border-box', pb: 1 }}
        >
            {/* Header with game selector */}
            <Box sx={{ flexShrink: 0 }}>
                <MultipleGameSelectorDialog
                    games={games}
                    setGames={setGames}
                    target={props.target}
                    team={team}
                    setTeam={setTeam}
                    sameMap={true}
                />
            </Box>

            <Grid container spacing={1} sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                {/* Left Panel - Filters */}
                <Grid size={3} sx={{ height: '100%', minHeight: 0 }}>
                    <NeonCard sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                        <NeonCardHeader title="Filters" sx={{ pb: 0.5 }} />
                        <NeonCardContent sx={{ flex: 1, minHeight: 0, ...scrollable }}>
                            <Stack spacing={1.5}>
                                {/* Map Selection (if multiple maps) */}
                                {uniqueMaps.length > 1 && (
                                    <Box>
                                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', mb: 1, display: 'block' }}>
                                            Map
                                        </Typography>
                                        <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                                            {uniqueMaps.map(mapId => {
                                                const map = context.mapsReferential.find(m => m.id === mapId);
                                                return (
                                                    <Chip
                                                        key={mapId}
                                                        label={capitalize(mapId)}
                                                        size="small"
                                                        onClick={() => setSelectedMap(map)}
                                                        sx={{
                                                            backgroundColor: selectedMap?.id === mapId 
                                                                ? `${neonColor}30` 
                                                                : 'rgba(255,255,255,0.08)',
                                                            border: selectedMap?.id === mapId 
                                                                ? `1px solid ${neonColor}` 
                                                                : '1px solid transparent',
                                                            '&:hover': {
                                                                backgroundColor: `${neonColor}20`,
                                                            }
                                                        }}
                                                    />
                                                );
                                            })}
                                        </Stack>
                                    </Box>
                                )}

                                {/* Display Mode */}
                                <Box>
                                    <NeonTabs 
                                        value={displayMode} 
                                        onChange={(_, v) => setDisplayMode(v)}
                                        sx={{ minHeight: '36px' }}
                                    >
                                        <NeonTab 
                                            value="kills" 
                                            label="Kills" 
                                            sx={{ minHeight: '36px', py: 0 }}
                                        />
                                        <NeonTab 
                                            value="deaths" 
                                            label="Deaths" 
                                            sx={{ minHeight: '36px', py: 0 }}
                                        />
                                        <NeonTab 
                                            value="both" 
                                            label="Both" 
                                            sx={{ minHeight: '36px', py: 0 }}
                                        />
                                    </NeonTabs>
                                </Box>

                                {/* Side Filter */}
                                <Box>
                                    <Stack direction="row" spacing={0.5}>
                                        <NeonToggleButton
                                            value="all"
                                            selected={sideFilter === 'all'}
                                            onClick={() => setSideFilter('all')}
                                            sx={{ flex: 1, fontSize: '0.75rem' }}
                                        >
                                            All
                                        </NeonToggleButton>
                                        <NeonToggleButton
                                            value="Attacker"
                                            selected={sideFilter === 'Attacker'}
                                            onClick={() => setSideFilter('Attacker')}
                                            sx={{ 
                                                flex: 1, 
                                                fontSize: '0.75rem',
                                                '&.Mui-selected': {
                                                    backgroundColor: `${attacker_color}50`,
                                                    borderColor: attacker_color,
                                                }
                                            }}
                                        >
                                            ATK
                                        </NeonToggleButton>
                                        <NeonToggleButton
                                            value="Defender"
                                            selected={sideFilter === 'Defender'}
                                            onClick={() => setSideFilter('Defender')}
                                            sx={{ 
                                                flex: 1, 
                                                fontSize: '0.75rem',
                                                '&.Mui-selected': {
                                                    backgroundColor: `${defender_color}50`,
                                                    borderColor: defender_color,
                                                }
                                            }}
                                        >
                                            DEF
                                        </NeonToggleButton>
                                    </Stack>
                                </Box>

                                {/* Player Selection */}
                                <Box>
                                    <Stack spacing={0.5}>
                                        {sortedTeamPlayers.map(player => (
                                            <NeonToggleButton
                                                key={player.id}
                                                value={player.id}
                                                selected={players.includes(player.id)}
                                                onClick={() => togglePlayer(player.id)}
                                                sx={{
                                                    justifyContent: 'flex-start',
                                                    px: 1.5,
                                                    py: 0.75,
                                                }}
                                            >
                                                <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
                                                    <Box sx={{
                                                        width: 10,
                                                        height: 10,
                                                        borderRadius: '50%',
                                                        background: getPlayerPointColor(player.id),
                                                        boxShadow: `0 0 6px ${getPlayerPointColor(player.id)}80`,
                                                        flexShrink: 0,
                                                    }} />
                                                    <Typography sx={{ fontSize: '0.8rem', flex: 1, textAlign: 'left' }}>
                                                        {player.id}
                                                    </Typography>
                                                    <Stack direction="row" spacing={0.25} alignItems="center">
                                                        {player.agents.map(agent => (
                                                            <AgentIcon key={agent} agent_name={agent} sx={{ width: 24, height: 24 }} />
                                                        ))}
                                                    </Stack>
                                                    <Typography sx={{
                                                        fontSize: '0.7rem',
                                                        color: 'rgba(255,255,255,0.45)',
                                                        minWidth: 36,
                                                        textAlign: 'right',
                                                    }}>
                                                        {player.kills}/{player.deaths}
                                                    </Typography>
                                                </Stack>
                                            </NeonToggleButton>
                                        ))}
                                        {sortedTeamPlayers.length === 0 && (
                                            <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.3)', fontStyle: 'italic' }}>
                                                {team ? 'Select games to see players who played the map' : 'Select a team to see players'}
                                            </Typography>
                                        )}
                                    </Stack>
                                </Box>

                                {/* Stats Summary */}
                                {games.length > 0 && (
                                    <Box sx={{ 
                                        mt: 1, 
                                        p: 1.25, 
                                        borderRadius: '8px',
                                        background: 'rgba(255,255,255,0.03)',
                                        border: '1px solid rgba(255,255,255,0.08)',
                                    }}>
                                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', mb: 1, display: 'block' }}>
                                            Summary
                                        </Typography>
                                        <Stack spacing={0.75}>
                                            <Stack direction="row" justifyContent="space-between">
                                                <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)' }}>
                                                    Games
                                                </Typography>
                                                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                                                    {games.filter(g => !selectedMap || g.map === selectedMap.id).length}
                                                </Typography>
                                            </Stack>
                                            <Stack direction="row" justifyContent="space-between">
                                                <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)' }}>
                                                    Rounds
                                                </Typography>
                                                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                                                    {roundSummaries.length}
                                                </Typography>
                                            </Stack>
                                            <Stack direction="row" justifyContent="space-between">
                                                <Typography sx={{ fontSize: '0.75rem', color: '#2ecc71' }}>
                                                    Kills
                                                </Typography>
                                                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#2ecc71' }}>
                                                    {totalKills}
                                                </Typography>
                                            </Stack>
                                            <Stack direction="row" justifyContent="space-between">
                                                <Typography sx={{ fontSize: '0.75rem', color: '#e74c3c' }}>
                                                    Deaths
                                                </Typography>
                                                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#e74c3c' }}>
                                                    {totalDeaths}
                                                </Typography>
                                            </Stack>
                                            <Stack direction="row" justifyContent="space-between">
                                                <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)' }}>
                                                    Headshots
                                                </Typography>
                                                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                                                    {headshotKills}
                                                </Typography>
                                            </Stack>
                                            <Stack direction="row" justifyContent="space-between">
                                                <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)' }}>
                                                    First Bloods
                                                </Typography>
                                                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                                                    {firstBloods}
                                                </Typography>
                                            </Stack>
                                        </Stack>
                                    </Box>
                                )}
                            </Stack>
                        </NeonCardContent>
                    </NeonCard>
                </Grid>

                {/* Center Panel - Map Heatmap */}
                <Grid size={6} sx={{ height: '100%', minHeight: 0 }}>
                    <NeonCard
                        sx={{
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            minHeight: 0,
                            overflow: 'hidden',
                            ...(selectedMap ? {
                                '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    inset: 0,
                                    zIndex: 0,
                                    pointerEvents: 'none',
                                    backgroundImage: `url(${getMapAsset(selectedMap.id, 'stylizedBackgroundImage')})`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center',
                                    opacity: 0.55,
                                    WebkitMaskImage:
                                        'radial-gradient(ellipse 82% 78% at 50% 45%, #000 0%, #000 48%, transparent 88%)',
                                    maskImage:
                                        'radial-gradient(ellipse 82% 78% at 50% 45%, #000 0%, #000 48%, transparent 88%)',
                                },
                            } : {}),
                        }}
                    >
                        <NeonCardHeader 
                            title={
                                <Stack direction="row" alignItems="center" spacing={2}>
                                    <GpsFixed sx={{ fontSize: '1.2rem', color: neonColor }} />
                                    <Typography variant="h6">
                                        {selectedMap ? capitalize(selectedMap.id) : 'Select a game'}
                                    </Typography>
                                    {filteredKillIndexes.length > 0 && (
                                        <Chip 
                                            size="small" 
                                            label={`${visibleKillEvents.length}/${filteredKillIndexes.length}`}
                                            sx={{ 
                                                backgroundColor: `${neonColor}20`,
                                                borderColor: neonColor,
                                                fontSize: '0.7rem',
                                            }}
                                        />
                                    )}
                                </Stack>
                            }
                            action={
                                <Stack direction="row" spacing={1} sx={{ pr: 1 }} alignItems="center">
                                    <Stack direction="row" spacing={0.5}>
                                        <NeonToggleButton
                                            value="victim"
                                            selected={positionSource === 'victim'}
                                            onClick={() => setPositionSource('victim')}
                                            sx={{ fontSize: '0.7rem', px: 1.25, minHeight: 32, height: 32, py: 0 }}
                                        >
                                            Victim
                                        </NeonToggleButton>
                                        <NeonToggleButton
                                            value="killer"
                                            selected={positionSource === 'killer'}
                                            onClick={() => setPositionSource('killer')}
                                            sx={{ fontSize: '0.7rem', px: 1.25, minHeight: 32, height: 32, py: 0 }}
                                        >
                                            Killer
                                        </NeonToggleButton>
                                    </Stack>
                                    <Stack direction="row" spacing={0.5}>
                                        <NeonToggleButton
                                            value="heatmap"
                                            selected={renderMode === 'heatmap'}
                                            onClick={() => setRenderMode('heatmap')}
                                            sx={{ fontSize: '0.7rem', px: 1.25, minHeight: 32, height: 32, py: 0 }}
                                        >
                                            Heatmap
                                        </NeonToggleButton>
                                        <NeonToggleButton
                                            value="points"
                                            selected={renderMode === 'points'}
                                            onClick={() => setRenderMode('points')}
                                            sx={{ fontSize: '0.7rem', px: 1.25, minHeight: 32, height: 32, py: 0 }}
                                        >
                                            Points
                                        </NeonToggleButton>
                                    </Stack>
                                </Stack>
                            }
                            sx={{ pb: 0.5, overflow: 'visible', alignItems: 'center' }}
                        />
                        <NeonCardContent sx={{ flex: 1, minHeight: 0, p: 1.5, overflow: 'hidden' }}>
                            {selectedMap ? (
                                <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
                                    {loading ? (
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            height: '100%',
                                        }}>
                                            <Typography sx={{ color: 'rgba(255,255,255,0.5)' }}>
                                                Loading...
                                            </Typography>
                                        </Box>
                                    ) : mapSelectableKills.length > 0 ? (
                                        <HeatmapKillsRender
                                            map={selectedMap}
                                            id={selectedMap.id}
                                            kills={mapSelectableKills}
                                            mode={renderMode}
                                            positionSource={positionSource}
                                            onMarqueeSelect={handleMarqueeSelect}
                                            highlightedIndex={hoveredEventIndex}
                                        />
                                    ) : (
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            height: '100%',
                                            flexDirection: 'column',
                                            gap: 2,
                                        }}>
                                            <VisibilityOff sx={{ fontSize: '3rem', color: 'rgba(255,255,255,0.2)' }} />
                                            <Typography sx={{ color: 'rgba(255,255,255,0.4)', textAlign: 'center' }}>
                                                No kill events found for the selected filters.
                                                <br />
                                                Try selecting different games or adjusting filters.
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            ) : (
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    height: '100%',
                                    flexDirection: 'column',
                                    gap: 2,
                                }}>
                                    <Visibility sx={{ fontSize: '3rem', color: 'rgba(255,255,255,0.2)' }} />
                                    <Typography sx={{ color: 'rgba(255,255,255,0.4)', textAlign: 'center' }}>
                                        Select games to view kill heatmap.
                                        <br />
                                        Use the "Change Game Set" button above.
                                    </Typography>
                                </Box>
                            )}
                        </NeonCardContent>
                    </NeonCard>
                </Grid>

                {/* Right Panel - Kill list */}
                <Grid size={3} sx={{ height: '100%', minHeight: 0 }}>
                    <NeonCard sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                        <NeonCardHeader
                            title={
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                        Events
                                    </Typography>
                                    {filteredKillIndexes.length > 0 && (
                                        <Chip
                                            size="small"
                                            label={`${visibleKillEvents.length}/${filteredKillIndexes.length}`}
                                            sx={{
                                                backgroundColor: `${neonColor}20`,
                                                borderColor: neonColor,
                                                fontSize: '0.7rem',
                                            }}
                                        />
                                    )}
                                </Stack>
                            }
                            action={
                                filteredKillIndexes.length > 0 ? (
                                    <Stack direction="row" spacing={0.5} sx={{ pr: 0.5 }}>
                                        <IconButton
                                            size="small"
                                            onClick={() => setAllKillEventsEnabled(true)}
                                            disabled={allKillsEnabled}
                                            sx={{ color: allKillsEnabled ? 'rgba(255,255,255,0.2)' : neonColor }}
                                            title="Enable all"
                                        >
                                            <Visibility sx={{ fontSize: '1rem' }} />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            onClick={() => setAllKillEventsEnabled(false)}
                                            disabled={noKillsEnabled}
                                            sx={{ color: noKillsEnabled ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.6)' }}
                                            title="Disable all"
                                        >
                                            <VisibilityOff sx={{ fontSize: '1rem' }} />
                                        </IconButton>
                                    </Stack>
                                ) : undefined
                            }
                            sx={{ pb: 0.5 }}
                        />
                        <NeonCardContent sx={{ flex: 1, minHeight: 0, p: 1, display: 'flex', flexDirection: 'column', gap: 1, overflow: 'hidden' }}>
                            <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                                <NeonToggleButton
                                    value="all"
                                    selected={plantFilter === 'all'}
                                    onClick={() => setPlantFilter('all')}
                                    sx={{ flex: 1, fontSize: '0.7rem', py: 0.5 }}
                                >
                                    All
                                </NeonToggleButton>
                                <NeonToggleButton
                                    value="pre"
                                    selected={plantFilter === 'pre'}
                                    onClick={() => setPlantFilter('pre')}
                                    sx={{ flex: 1, fontSize: '0.7rem', py: 0.5 }}
                                >
                                    Pre
                                </NeonToggleButton>
                                <NeonToggleButton
                                    value="post"
                                    selected={plantFilter === 'post'}
                                    onClick={() => setPlantFilter('post')}
                                    sx={{ flex: 1, fontSize: '0.7rem', py: 0.5 }}
                                >
                                    Post
                                </NeonToggleButton>
                            </Box>
                            {renderWeaponFilter(
                                'Killer weapon',
                                availableKillerWeapons,
                                selectedKillerWeapons,
                                (weapon) => setSelectedKillerWeapons((prev) => toggleSetValue(prev, weapon)),
                                () => setSelectedKillerWeapons(new Set()),
                            )}
                            {renderWeaponFilter(
                                'Victim weapon',
                                availableVictimWeapons,
                                selectedVictimWeapons,
                                (weapon) => setSelectedVictimWeapons((prev) => toggleSetValue(prev, weapon)),
                                () => setSelectedVictimWeapons(new Set()),
                            )}
                            <Box sx={{
                                flex: 1,
                                minHeight: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1,
                                ...scrollable,
                            }}>
                                {killEventGroups.map((group) => {
                                    const collapsed = collapsedEcoTypes.has(group.ecoType);
                                    const enabledCount = group.indexes.filter((index) => !disabledKillIndexes.has(index)).length;
                                    const allEnabled = enabledCount === group.indexes.length;
                                    const noneEnabled = enabledCount === 0;

                                    return (
                                        <Box
                                            key={group.ecoType}
                                            sx={{
                                                flexShrink: 0,
                                                borderRadius: '8px',
                                                border: '1px solid rgba(255,255,255,0.08)',
                                                background: 'rgba(255,255,255,0.02)',
                                                overflow: 'hidden',
                                                position: 'relative',
                                                zIndex: 0,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 0.5,
                                                    px: 1,
                                                    py: 0.75,
                                                    background: `linear-gradient(90deg, ${neonColor}18 0%, rgba(255,255,255,0.03) 100%)`,
                                                    borderBottom: collapsed ? 'none' : '1px solid rgba(255,255,255,0.08)',
                                                }}
                                            >
                                                <IconButton
                                                    size="small"
                                                    onClick={() => toggleEcoTypeCollapsed(group.ecoType)}
                                                    sx={{ color: 'rgba(255,255,255,0.7)', p: 0.25 }}
                                                    title={collapsed ? 'Expand' : 'Collapse'}
                                                >
                                                    {collapsed
                                                        ? <ExpandMore sx={{ fontSize: '1.05rem' }} />
                                                        : <ExpandLess sx={{ fontSize: '1.05rem' }} />
                                                    }
                                                </IconButton>
                                                <Box
                                                    onClick={() => toggleEcoTypeCollapsed(group.ecoType)}
                                                    sx={{ flex: 1, minWidth: 0, cursor: 'pointer' }}
                                                >
                                                    <Typography sx={{
                                                        fontSize: '0.7rem',
                                                        fontWeight: 700,
                                                        color: 'rgba(255,255,255,0.9)',
                                                        letterSpacing: '0.5px',
                                                        textTransform: 'uppercase',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                    }}>
                                                        {group.ecoType}
                                                    </Typography>
                                                </Box>
                                                <Typography sx={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.45)', pr: 0.25 }}>
                                                    {enabledCount}/{group.indexes.length}
                                                </Typography>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => setEcoTypeEnabled(group.indexes, true)}
                                                    disabled={allEnabled}
                                                    sx={{ color: allEnabled ? 'rgba(255,255,255,0.2)' : neonColor, p: 0.25 }}
                                                    title={`Enable all ${group.ecoType}`}
                                                >
                                                    <Visibility sx={{ fontSize: '0.95rem' }} />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => setEcoTypeEnabled(group.indexes, false)}
                                                    disabled={noneEnabled}
                                                    sx={{ color: noneEnabled ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.6)', p: 0.25 }}
                                                    title={`Disable all ${group.ecoType}`}
                                                >
                                                    <VisibilityOff sx={{ fontSize: '0.95rem' }} />
                                                </IconButton>
                                            </Box>

                                            {!collapsed && (
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, p: 0.75 }}>
                                                    {group.indexes.map((index) => {
                                                        const event = killEvents[index];
                                                        const enabled = !disabledKillIndexes.has(index);
                                                        const accent = event.eventType === 'kill' ? '#2ecc71' : '#e74c3c';
                                                        const isKill = event.eventType === 'kill';
                                                        const killerName = displayPlayerName(isKill ? event.player : null);
                                                        const victimName = displayPlayerName(isKill ? event.victim_player : event.player);
                                                        const killerTeam = isKill ? event.ownTeam : event.opponent;
                                                        const victimTeam = isKill ? event.opponent : event.ownTeam;
                                                        return (
                                                            <Box
                                                                key={index}
                                                                onClick={() => toggleKillEvent(index)}
                                                                onMouseEnter={() => setHoveredEventIndex(index)}
                                                                onMouseLeave={() => setHoveredEventIndex((prev) => (prev === index ? null : prev))}
                                                                sx={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: 0.75,
                                                                    px: 1,
                                                                    py: 0.75,
                                                                    borderRadius: '6px',
                                                                    cursor: 'pointer',
                                                                    opacity: enabled ? 1 : 0.4,
                                                                    background: hoveredEventIndex === index
                                                                        ? 'rgba(255,255,255,0.1)'
                                                                        : enabled ? 'rgba(255,255,255,0.04)' : 'transparent',
                                                                    border: `1px solid ${hoveredEventIndex === index ? `${accent}90` : enabled ? `${accent}40` : 'rgba(255,255,255,0.05)'}`,
                                                                    boxShadow: hoveredEventIndex === index ? `0 0 12px ${accent}30` : 'none',
                                                                    transition: 'background 0.15s ease, opacity 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease',
                                                                    '&:hover': {
                                                                        background: 'rgba(255,255,255,0.1)',
                                                                        opacity: 1,
                                                                    },
                                                                }}
                                                            >
                                                                {enabled
                                                                    ? <Visibility sx={{ fontSize: '0.95rem', color: accent, flexShrink: 0 }} />
                                                                    : <VisibilityOff sx={{ fontSize: '0.95rem', color: 'rgba(255,255,255,0.35)', flexShrink: 0 }} />
                                                                }

                                                                {/* Killer context (left) */}
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, minWidth: 0 }}>
                                                                    {event.killer_weapon
                                                                        ? <WeaponIcon weapon={event.killer_weapon} width={28} height={16} />
                                                                        : <Box sx={{ width: 28, height: 16, flexShrink: 0 }} />
                                                                    }
                                                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                                                        <Typography sx={{
                                                                            fontSize: '0.7rem',
                                                                            fontWeight: 600,
                                                                            color: 'rgba(255,255,255,0.85)',
                                                                            overflow: 'hidden',
                                                                            textOverflow: 'ellipsis',
                                                                            whiteSpace: 'nowrap',
                                                                        }}>
                                                                            {killerName || '—'}
                                                                        </Typography>
                                                                        {killerTeam && (
                                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.35 }}>
                                                                                <TeamIcon team_name={killerTeam} sx={{ width: 12, height: 12, flexShrink: 0 }} />
                                                                            </Box>
                                                                        )}
                                                                    </Box>
                                                                    <AgentIcon agent_name={event.killer_agent || ''} sx={{ width: 22, height: 22, flexShrink: 0, opacity: event.killer_agent ? 1 : 0.3 }} />
                                                                </Box>

                                                                {/* Center meta */}
                                                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, px: 0.25 }}>
                                                                    <Typography sx={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.35)', lineHeight: 1 }}>→</Typography>
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.35, mt: 0.15 }}>
                                                                        <Typography sx={{
                                                                            fontSize: '0.5rem',
                                                                            fontWeight: 700,
                                                                            color: event.plantPhase === 'post' ? '#e67e22' : '#3498db',
                                                                        }}>
                                                                            {event.plantPhase === 'post' ? 'POST' : 'PRE'}
                                                                        </Typography>
                                                                        <Typography sx={{ fontSize: '0.55rem', color: 'rgba(255,255,255,0.45)' }}>
                                                                            {formatDuration(event.time_in_round)}
                                                                        </Typography>
                                                                        {event.is_headshot && (
                                                                            <Typography sx={{ fontSize: '0.5rem', color: neonColor, fontWeight: 700 }}>HS</Typography>
                                                                        )}
                                                                        {event.is_first_blood && (
                                                                            <Typography sx={{ fontSize: '0.5rem', color: '#f1c40f', fontWeight: 700 }}>FB</Typography>
                                                                        )}
                                                                    </Box>
                                                                </Box>

                                                                {/* Victim context (right, mirrored) */}
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, minWidth: 0, justifyContent: 'flex-end' }}>
                                                                    <AgentIcon agent_name={event.victim_agent} sx={{ width: 22, height: 22, flexShrink: 0 }} />
                                                                    <Box sx={{ minWidth: 0, flex: 1, textAlign: 'right' }}>
                                                                        <Typography sx={{
                                                                            fontSize: '0.7rem',
                                                                            fontWeight: 600,
                                                                            color: 'rgba(255,255,255,0.85)',
                                                                            overflow: 'hidden',
                                                                            textOverflow: 'ellipsis',
                                                                            whiteSpace: 'nowrap',
                                                                        }}>
                                                                            {victimName || '—'}
                                                                        </Typography>
                                                                        {victimTeam && (
                                                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.35 }}>
                                                                                <TeamIcon team_name={victimTeam} sx={{ width: 12, height: 12, flexShrink: 0 }} />
                                                                            </Box>
                                                                        )}
                                                                    </Box>
                                                                    {event.victim_weapon
                                                                        ? <WeaponIcon weapon={event.victim_weapon} width={28} height={16} />
                                                                        : <Box sx={{ width: 28, height: 16, flexShrink: 0 }} />
                                                                    }
                                                                </Box>
                                                            </Box>
                                                        );
                                                    })}
                                                </Box>
                                            )}
                                        </Box>
                                    );
                                })}
                                {filteredKillIndexes.length === 0 && (
                                    <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.3)', fontStyle: 'italic', px: 0.5 }}>
                                        {loading
                                            ? 'Loading events...'
                                            : killEvents.length > 0
                                                ? 'No events match current filters'
                                                : 'No events to display'}
                                    </Typography>
                                )}
                            </Box>
                        </NeonCardContent>
                    </NeonCard>
                </Grid>
            </Grid>
        </WindowContainer>
    );
}
